import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components/ButtonLoader";
import { MultiCheckDropdown } from "Components/MultiCheckDropdown";
import { authService } from "Service/Axios";
import { Validator } from "Utils/validator";
import { CREATE_ADMIN_FIELD_URL, UPDATE_ADMIN_FIELD_URL } from "Constant/api";
import { DESIGN_TYPE_LIST, FEILD_TYPE_LIST } from "Constant/constants";

export const CreateField = ({
  show,
  toggle,
  selectedData,
  fetchList,
  projectList,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    global: "global",
    projectIds: "",
    fieldName: "",
    fieldLabel: "",
    fieldType: "",
    designType: "",
  });
  const [errMsg, setErrMsg] = useState({
    projectIds: "",
    fieldName: "",
    fieldLabel: "",
    fieldType: "",
    designType: "",
  });

  const { global, projectIds, fieldName, fieldLabel, fieldType, designType } =
    formData;

  // On Change
  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
      if (e.target.name === "fieldName") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            fieldName: t("common.messages.error.adminSettings.fieldName.blank"),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            fieldName: "",
          }));
        }
      }

      if (e.target.name === "fieldLabel") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            fieldLabel: t(
              "common.messages.error.adminSettings.fieldLabel.blank"
            ),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            fieldLabel: "",
          }));
        }
      }

      if (e.target.name === "fieldType") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            fieldType: t("common.messages.error.adminSettings.fieldType.blank"),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            fieldType: "",
          }));
        }
      }

      if (e.target.name === "designType") {
        if (!Validator.text(e.target.value)) {
          setErrMsg((prev) => ({
            ...prev,
            designType: t(
              "common.messages.error.adminSettings.designType.blank"
            ),
          }));
        } else {
          setErrMsg((prev) => ({
            ...prev,
            designType: "",
          }));
        }
      }
    }
  };

  const handleChangeMultiple = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected,
    }));

    if (name === "projectIds") {
      if (!Validator.hasMultiple(selected)) {
        setErrMsg((prev) => ({
          ...prev,
          projectIds: t("common.messages.error.adminSettings.projectIds.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          projectIds: "",
        }));
      }
    }
  };

  useEffect(() => {
    if (selectedData) {
      setFormData({
        global: selectedData.global ? "global" : "project",
        projectIds:
          projectList &&
          projectList?.filter((obj) =>
            selectedData.projects?.includes(obj.value)
          ),
        fieldName: selectedData.questionLabel,
        fieldLabel: selectedData.questionLabel,
        fieldType: selectedData.questionType,
        designType: selectedData.designType,
      });
    }
  }, [selectedData, projectList]);

  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(fieldName) &&
        Validator.text(fieldLabel) &&
        Validator.text(fieldType) &&
        Validator.text(designType)
      ) {
        if (global !== "global") {
          if (Validator.hasMultiple(projectIds)) {
            resolve(true);
          } else {
            setErrMsg((prev) => ({
              ...prev,
              projectIds: t(
                "common.messages.error.adminSettings.projectIds.blank"
              ),
            }));
            resolve(false);
          }
        } else {
          resolve(true);
        }
      } else {
        if (!Validator.text(fieldName)) {
          setErrMsg((prev) => ({
            ...prev,
            fieldName: t("common.messages.error.adminSettings.fieldName.blank"),
          }));
        }
        if (!Validator.text(fieldLabel)) {
          setErrMsg((prev) => ({
            ...prev,
            fieldLabel: t(
              "common.messages.error.adminSettings.fieldLabel.blank"
            ),
          }));
        }
        if (!Validator.text(fieldType)) {
          setErrMsg((prev) => ({
            ...prev,
            fieldType: t("common.messages.error.adminSettings.fieldType.blank"),
          }));
        }
        if (!Validator.text(designType)) {
          setErrMsg((prev) => ({
            ...prev,
            designType: t(
              "common.messages.error.adminSettings.designType.blank"
            ),
          }));
        }
        resolve(false);
      }
    });
  };
  // On Submit
  const onSaveField = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          global: global === "global",
          questionName: fieldName,
          questionLabel: fieldLabel,
          questionType: fieldType,
          designType: designType,
        };
        if (global === "project") {
          payload.projects = projectIds?.map((option) => option.value);
        }
        if (selectedData) {
          payload.gid = selectedData.gid;
          const response = await authService(
            UPDATE_ADMIN_FIELD_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.updateField")
            );
          } else {
            toast.error(response.message);
          }
        } else {
          const response = await authService(
            CREATE_ADMIN_FIELD_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(
              t("common.messages.notification.success.createField")
            );
          } else {
            toast.error(response.message);
          }
        }
        setLoading(false);
        fetchList(projectIds);
        toggle();
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="justify-content-center">
        <Modal.Title>{t("auth.adminSettings.createField")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-12">
            <div className="mb-1">
              <label htmlFor="global" className="form-label me-3">
                {t("common.label.global")}
              </label>
              <div className="form-check form-check-inline">
                <input
                  type="radio"
                  id="global"
                  value="global"
                  checked={global === "global"}
                  name="global"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.yes")}
                </label>
              </div>

              <div className="form-check form-check-inline">
                <label htmlFor="global" className="form-check-label"></label>
                <input
                  type="radio"
                  id="global"
                  value="project"
                  checked={global === "project"}
                  name="global"
                  onChange={handleChange}
                  className="form-check-input"
                />
                <label className="form-check-label">
                  {t("common.label.no")}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={`mb-4 ${errMsg.projectIds ? "error" : ""}`}>
          <label htmlFor="projectIds" className="form-label">
            {t("common.label.projects")}
          </label>
          <MultiCheckDropdown
            placeholder={t("common.placeHolder.projects")}
            options={projectList}
            value={projectIds}
            onChange={(selected) =>
              handleChangeMultiple(selected, "projectIds")
            }
            disabled={global === "global"}
          />
          {errMsg.projectIds && <p>{errMsg.projectIds}</p>}
        </div>
        <div className={`mb-4 ${errMsg.fieldName ? "error" : ""}`}>
          <label htmlFor="fieldName" className="form-label">
            {t("common.label.field")}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.placeHolder.field")}
            id="fieldName"
            name="fieldName"
            value={fieldName}
            onChange={handleChange}
          />
          {errMsg.fieldName && <p>{errMsg.fieldName}</p>}
        </div>
        <div className={`mb-4 ${errMsg.fieldLabel ? "error" : ""}`}>
          <label htmlFor="fieldLabel" className="form-label">
            {t("common.label.label")}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.placeHolder.label")}
            id="fieldLabel"
            name="fieldLabel"
            value={fieldLabel}
            onChange={handleChange}
          />
          {errMsg.fieldLabel && <p>{errMsg.fieldLabel}</p>}
        </div>
        <div className={`mb-4 ${errMsg.fieldType ? "error" : ""}`}>
          <label htmlFor="fieldType" className="form-label">
            {t("common.label.fieldType")}
          </label>
          <select
            className="form-select"
            id="fieldType"
            name="fieldType"
            value={fieldType}
            onChange={handleChange}
          >
            <option>- Select -</option>
            {FEILD_TYPE_LIST.map((fType) => (
              <option value={fType.key} key={fType.key}>
                {fType.name}
              </option>
            ))}
          </select>
          {errMsg.fieldType && <p>{errMsg.fieldType}</p>}
        </div>
        <div className={`mb-4 ${errMsg.designType ? "error" : ""}`}>
          <label htmlFor="designType" className="form-label">
            {t("common.label.designType")}
          </label>
          <select
            className="form-select"
            id="designType"
            name="designType"
            value={designType}
            onChange={handleChange}
          >
            <option>- Select -</option>
            {DESIGN_TYPE_LIST.map((fType) => (
              <option value={fType.key} key={fType.key}>
                {fType.name}
              </option>
            ))}
          </select>
          {errMsg.designType && <p>{errMsg.designType}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          {t("common.button.close")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSaveField}>
            {t("common.button.save")}
          </Button>
        ) : (
          <ButtonLoader className="btn-loading-sm" />
        )}
      </Modal.Footer>
    </Modal>
  );
};
